<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="md" :visible="modal.show" :data="model" :title="$t(`Approve Temporary Items`)"
                   @show="onShow" @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)"
                   :show-confirmation="showConfirmation" :ok-title="$t('Submit')" :is-loading="loading"
                   scrollable>

      <div class="row mb-3">
        <div class="col-md-12">
          <div v-if="model.customer">
            <h5 class="mb-0">
              {{ model.customer.display_name }}
            </h5>
            <template v-if="model.customer.email">
              <p class="m-0">
                {{ model.customer.email }}
              </p>
            </template>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <receipt-select-input-widget :model="model" attribute="receipt_uuid" hint with-dummy-receipt
                                       :info-text="warningText"
                                       expand="department,miscellaneous_items,recommended_by,specialists"
                                       @select="fillTempInventoryItemApproveModel"
                                       @unselect="onReceiptUnselect"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="date" type="datepicker"/>
        </div>
        <div class="col-md-6">
          <department-select-input-widget :model="model" attribute="department_uuid" @select="onDepartmentSelect" @unselect="onDepartmentUnselect"/>
        </div>
        <div class="col-md-6">
          <customer-select-input-widget :model="model" attribute="recommended_by_uuid"/>
        </div>
        <div class="col-md-12">
          <user-select-input-widget :model="model" attribute="specialists_uuids" multiple
                                    :items="model.specialists" :disabled="!model.department_uuid"
                                    :filter-extra-data="specialistExtraFilters"/>
        </div>
        <div class="col-md-12">
          <input-widget :model="model" attribute="description" type="textarea"/>
        </div>
        <div class="col-md-12">
          <input-widget :model="model" attribute="message" type="ckeditor"/>
        </div>
      </div>

      <div class="row mb-2" hidden>
        <div class="col-md-12">
          <b-button class="p-0 mb-2" variant="link" @click="onNewMiscellaneousItemClick">
            <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
            {{ $t('Add Miscellaneous Charges') }}
          </b-button>
        </div>
        <div class="col-md-12" v-if="model.miscellaneous_items.length > 0">
          <div class="row">
            <div class="col-md-4">
              <h6>{{ $t('Description') }}</h6>
            </div>
            <div class="col-md-4">
              <h6>{{ $t('Amount') }}</h6>
            </div>
            <div class="col-md-2">
              <h6>{{ $t('Taxable') }}</h6>
            </div>
          </div>
          <div class="row" :key="`miscellaneous-item-${index}`" v-for="(item, index) in model.miscellaneous_items">
            <div class="col-md-4">
              <input-widget :rules="item.amount ? 'required|max:255' : 'max:255'" :model="item" attribute="description"
                            :label="false"/>
            </div>
            <div class="col-md-4">
              <currency-input-widget attribute="amount" :rules="item.description ? 'required' : ''"
                                     :model="item" :label="false"/>
            </div>
            <div class="col-md-3">
              <input-widget attribute="taxable" :model="item" type="checkbox" :label="false"/>
            </div>
            <div class="col-md-1">
              <b-tooltip :target="`remove-misc-item-button-${index}`" :title="$t('Delete')"/>
              <font-awesome-icon class="text-danger hover-cursor" :id="`remove-misc-item-button-${index}`"
                                 @click="onMiscellaneousItemDelete(index)" style="font-size: 1.5em;" :icon="'times'"/>
            </div>
          </div>
        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import InputWidget from "../../../core/components/input-widget/InputWidget";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal";
  import TemporaryItemsApproveModel from "@/modules/items/temporary/TemporaryItemsApprove.model";
  import MiscellaneousItem from "@/modules/receipt/MiscellaneousItem.model";
  import DepartmentSelectInputWidget from "@/core/widgets/DepartmentSelectInputWidget";
  import CustomerSelectInputWidget from "@/core/widgets/CustomerSelectInputWidget";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget";
  import ReceiptSelectInputWidget from "@/core/widgets/ReceiptSelectInputWidget";
  import UserSelectInputWidget from "../../../core/widgets/UserSelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('temporaryItems');

  export default {
    name: "TemporaryItemsApproveModal",
    components: {
      UserSelectInputWidget,
      ReceiptSelectInputWidget,
      CurrencyInputWidget,
      CustomerSelectInputWidget,
      DepartmentSelectInputWidget,
      InputWidget, ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new TemporaryItemsApproveModel(),
        errorMessage: null,
        warningText: '',
      }
    },
    computed: {
      ...mapState({
        modal: state => state.temporaryItemsApproveModal,
        editObject: state => state.temporaryItemsApproveModal.object
      }),
      specialistExtraFilters() {
        return {specialist_department_uuid: this.model && this.model.department_uuid ? this.model.department_uuid : null};
      }
    },
    watch: {
      editObject() {
        this.model = new TemporaryItemsApproveModel(this.editObject);
      },
      'model.recommended_by_uuid': {
        deep: true,
        async handler() {
          this.validateCustomerAndRecommendedBy();
        }
      }
    },
    methods: {
      ...mapActions(['hideTemporaryItemsApproveModal', 'approveItems']),
      onShow() {
        this.showConfirmation = true;
        this.warningText = '';
        this.model.customer_uuid = this.model.customer.uuid;
      },
      onHidden() {
        this.model = new TemporaryItemsApproveModel();
        this.hideTemporaryItemsApproveModal();
      },
      async onSubmit() {
        this.showConfirmation = false;
        let success = await this.$confirm(this.$i18n.t('Are you sure you want to approve {count} temporary item(s)?', {count: this.model.uuids.length}))
        if (!success) {
          return;
        }
        this.model.customer_uuid = this.model.customer.uuid;
        let response = await this.approveItems(this.model)
        if (response.success) {
          this.hideTemporaryItemsApproveModal();
        } else {
          this.showConfirmation = true;
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0])
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
            let responseBody = response.body;
            responseBody.receipt_uuid = responseBody.receipt_no
            this.model.setMultipleErrors(responseBody);
            for (let index in responseBody.miscellaneous_items) {
              this.model.miscellaneous_items[index].setMultipleErrors(responseBody.miscellaneous_items[index]);
            }
          }
        }
      },
      onMiscellaneousItemDelete(index) {
        for (let i = 0; i < this.model.miscellaneous_items.length; i++) {
          if (i === index) {
            this.model.miscellaneous_items.splice(i, 1);
            break;
          }
        }
      },
      onNewMiscellaneousItemClick() {
        this.model.miscellaneous_items.push(new MiscellaneousItem({taxable: true}))
      },
      validateCustomerAndRecommendedBy() {
        if (this.model.customer_uuid && this.model.recommended_by_uuid
          && this.model.customer_uuid === this.model.recommended_by_uuid) {
          this.model.addError('recommended_by_uuid', this.$t('Recommended by user cannot be same as the Vendor'));
        } else {
          this.model.resetErrors('recommended_by_uuid');
        }
      },
      fillTempInventoryItemApproveModel(receipt) {
        let data = {
          "receipt_no": receipt.receipt_no,
          "uuids": this.model && this.model.uuids,
          "customer_uuid": this.model && this.model.customer_uuid,
          "customer": this.model && this.model.customer,
        }
        this.warningText = this.$i18n.t('Item(s) will be added to a new receipt.')

        if (!receipt.is_dummy) {
          this.warningText = this.$i18n.t('Item(s) will be added to an existing receipt.')

          let specialists = receipt.specialists
          let specialists_uuids = specialists ? receipt.specialists.map(s => s.uuid) : []

          data = {
            ...data,
            "receipt_no": receipt.receipt_no,
            "department": receipt.department,
            "department_uuid": receipt.department_uuid,
            "recommended_by_uuid": receipt.recommended_by_uuid,
            "recommended_by": receipt.recommended_by,
            "description": receipt.description,
            "specialists": specialists,
            "specialists_uuids": specialists_uuids,
            "miscellaneous_items": receipt.miscellaneous_items.map(item => new MiscellaneousItem(item)),
            "date": receipt.date
          }
        }

        this.model = new TemporaryItemsApproveModel(data);
      },
      onReceiptUnselect(receipt) {
        this.model = new TemporaryItemsApproveModel(this.editObject);
        this.warningText = '';
      },
      onDepartmentSelect() {
        this.model.specialists = [];
        this.model.specialists_uuids = [];
      },
      onDepartmentUnselect() {
        this.model.specialists = [];
        this.model.specialists_uuids = [];
      }
    }
  }
</script>

<style scoped>

</style>
